<script setup lang="ts">
  import { Typo } from '@travelata-libs/ui-kit';
  import { formatMoney } from '@travelata-libs/utils';
  import { computed, unref } from 'vue';
  import DirectionPoints from './DirectionPoints.vue';
  import DirectionType from './DirectionType.vue';
  import { FOR_FREE_TEXT } from '../lib';
  import {
    type DirectionModelValue,
    DirectionType as DirectionEnum,
    type DirectionOption,
    type Direction,
    type Point,
    type Type,
    type Passengers,
    type Option,
  } from '../Transfer.types';

  interface TransferDirectionProps {
    modelValue: DirectionModelValue;
    title: string;
    direction: `${Direction}`;
    passengers: Passengers;
    points: Point[];
    types: Type[];
    options: Option[];
  }

  interface TransferDirectionEmits {
    (e: 'update:modelValue', value: DirectionModelValue): void;
  }

  const props = defineProps<TransferDirectionProps>();

  const emit = defineEmits<TransferDirectionEmits>();

  const innerValue = computed({
    get() {
      return props.modelValue;
    },
    set(value: DirectionModelValue) {
      emit('update:modelValue', value);
    },
  });

  const passengersInfo = computed(() => {
    const { adultsCount, childrenCount, childrenAge } = props.passengers;

    const adultsText = adultsCount ? `${adultsCount} взр` : '';
    const childrenText = childrenCount ? ` + ${childrenCount} реб` : '';
    const childrenAgeText = childrenAge?.length ? ` (${childrenAge.join('+')})` : '';

    return `${adultsText}${childrenText}${childrenAgeText}`.trim();
  });

  const selectedOption = computed(() => {
    return props.options.find((option) => option.value === unref(innerValue).option);
  });

  const selectedType = computed(() => {
    return props.types.find((type) => type.value === unref(innerValue).type);
  });

  const calculatePrice = (type: Type, option: Option) => {
    if (type?.includedInPrice) {
      return 0;
    }

    if ([DirectionEnum.Public, DirectionEnum.Group].includes(type?.value)) {
      return type?.price || 0;
    } else if (type?.value === DirectionEnum.Individual) {
      return option?.includedInPrice ? 0 : option?.price || 0;
    }

    return 0;
  };

  const changeValue = (type: Type, option: Option) => {
    innerValue.value = {
      type: type.value,
      option: option.value,
      price: calculatePrice(type, option),
    };
  };

  const onChangeOption = (option: DirectionOption) => {
    const currentOption = props.options.find((item) => item.value === option);
    const type = unref(selectedType);

    if (type?.value && currentOption?.value) {
      changeValue(type, currentOption);
    }
  };

  const onChangeType = (type: DirectionEnum) => {
    const currentType = props.types.find((item) => item.value === type);
    const option = unref(selectedOption);

    if (currentType?.value && option?.value) {
      changeValue(currentType, option);
    }
  };

  const directionPriceInfo = computed(() => {
    const price = innerValue.value.price || 0;
    const type = unref(selectedType);
    const option = unref(selectedOption);

    if (type?.value === DirectionEnum.Individual && option?.includedInPrice) {
      return FOR_FREE_TEXT;
    }

    return type?.includedInPrice ? FOR_FREE_TEXT : formatMoney(price);
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div class="tl-transfer-direction">
    <header class="tl-transfer-direction__header">
      <Typo variant="h-5" class="tl-transfer-direction__title">{{ title }}</Typo>
      <Typo variant="body-m-400" class="tl-transfer-direction__passengers">{{ passengersInfo }}</Typo>
      <DirectionPoints :points="points" :direction="direction" class="tl-transfer-direction__points" />
      <Typo variant="h-6" class="tl-transfer-direction__price">{{ directionPriceInfo }}</Typo>
    </header>

    <div class="tl-transfer-direction__types">
      <DirectionType
        v-for="type in types"
        :key="type.value"
        :model-value="innerValue"
        :type="type"
        :passengers="passengers"
        :options="options"
        @change-option="onChangeOption"
        @change-type="onChangeType"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .tl-transfer-direction {
    @media (max-width: $breakpoint-mobile-max) {
      border-bottom: 1px dashed var(--color-text-tertiary);
      padding-bottom: var(--spacing-m);
    }

    &__header {
      align-items: center;
      display: grid;
      grid-gap: var(--spacing-xs);
      grid-template: repeat(2, auto) / auto 1fr;
      grid-template-areas:
        'title passengers'
        'points price';
      margin-bottom: var(--spacing-m);

      @media (max-width: $breakpoint-mobile-max) {
        align-items: start;
        grid-template: repeat(3, auto) / 1fr auto;
        grid-template-areas:
          'title price'
          'passengers passengers'
          'points points';
      }
    }

    &__title {
      grid-area: title;
    }

    &__passengers {
      color: var(--color-text-secondary);
      grid-area: passengers;
      justify-self: end;

      @media (max-width: $breakpoint-mobile-max) {
        justify-self: start;
      }
    }

    &__points {
      grid-area: points;
    }

    &__price {
      grid-area: price;
      justify-self: end;
    }

    &__types {
      border-bottom: 1px dashed var(--color-text-tertiary);
      border-top: 1px dashed var(--color-text-tertiary);
      display: grid;
      grid-gap: var(--spacing-s);
      grid-template: auto / repeat(3, 1fr);
      padding: var(--spacing-m) 0;

      @media (max-width: $breakpoint-mobile-max) {
        border: none;
        grid-gap: var(--spacing-l);
        grid-template: repeat(3, auto) / 1fr;
        padding: 0;
      }
    }
  }
</style>
